import type { CeoDebtScheduleItem } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { formatDateRu, formatMillions, formatMoney } from '../ceoFormat';
import { ScheduleStatus } from '../debts/ScheduleStatus';
import { hasPaymentMark, isRowPaid, rowAmounts, scheduleTotals } from '../debts/scheduleModel';
import {
  ActionsCell,
  DueSubtext,
  ScheduleHeader,
  SectionTitle,
  Table,
  TableWrap,
} from './styles';

interface DebtDetailScheduleTableProps {
  schedule: CeoDebtScheduleItem[];
  totalScheduledPrincipal: number;
  totalScheduledInterest: number;
  totalScheduledFees: number;
  can_write: boolean;
  onMarkPaid: (item: CeoDebtScheduleItem) => void;
  onUnmarkPaid: (item: CeoDebtScheduleItem) => void;
}

export function DebtDetailScheduleTable({
  schedule,
  totalScheduledPrincipal,
  totalScheduledInterest,
  totalScheduledFees,
  can_write,
  onMarkPaid,
  onUnmarkPaid,
}: DebtDetailScheduleTableProps) {
  const totals = scheduleTotals(schedule);
  return (
    <div>
      <ScheduleHeader>
        <SectionTitle>
          Полный график платежей ({schedule.length} траншей: основной долг{' '}
          {formatMillions(totalScheduledPrincipal)}, проценты/лизинг{' '}
          {formatMillions(totalScheduledInterest)}
          {totalScheduledFees > 0
            ? `, комиссии ${formatMillions(totalScheduledFees)}`
            : ''}
          )
        </SectionTitle>
      </ScheduleHeader>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>№</th>
              <th>Дата платежа</th>
              <th>Основной долг</th>
              <th>Проценты / Лизинг</th>
              <th>Комиссия</th>
              <th>По графику</th>
              <th>Оплачено</th>
              <th>Осталось</th>
              <th>Долг после платежа</th>
              <th>Статус</th>
              {can_write && <th>Действие</th>}
            </tr>
          </thead>
          <tbody>
            {schedule.map((item) => {
              const isPaid = isRowPaid(item);
              const amounts = rowAmounts(item);
              return (
                <tr key={item.id}>
                  <td>#{item.installment_no}</td>
                  <td>
                    <strong>{formatDateRu(item.due_date)}</strong>
                    {!isPaid && (
                      <DueSubtext>
                        {item.days_until_due < 0
                          ? `просрочено на ${Math.abs(item.days_until_due)} дн.`
                          : item.days_until_due === 0
                            ? 'сегодня'
                            : `через ${item.days_until_due} дн.`}
                      </DueSubtext>
                    )}
                  </td>
                  <td className="money">{formatMoney(item.principal_amount)}</td>
                  <td className="money">{formatMoney(item.interest_amount)}</td>
                  <td className="money">
                    {item.fee_amount > 0 ? formatMoney(item.fee_amount) : '—'}
                  </td>
                  <td className="money">{formatMoney(amounts.scheduled)}</td>
                  <td className="money">{amounts.paid > 0 ? formatMoney(amounts.paid) : '—'}</td>
                  <td className="money">
                    <strong>{formatMoney(amounts.left)}</strong>
                  </td>
                  <td className="money">{formatMoney(item.remaining_principal)}</td>
                  <td>
                    <ScheduleStatus item={item} />
                  </td>
                  {can_write && (
                    <td>
                      <ActionsCell>
                        {!isPaid && (
                          <Button $size="sm" type="button" onClick={() => onMarkPaid(item)}>
                            Отметить оплату
                          </Button>
                        )}
                        {hasPaymentMark(item) && (
                          <Button
                            $size="sm"
                            $variant="ghost"
                            type="button"
                            onClick={() => onUnmarkPaid(item)}
                          >
                            Снять отметку
                          </Button>
                        )}
                      </ActionsCell>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          {schedule.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={2}>Итого</td>
                <td className="money">{formatMoney(totalScheduledPrincipal)}</td>
                <td className="money">{formatMoney(totalScheduledInterest)}</td>
                <td className="money">
                  {totalScheduledFees > 0 ? formatMoney(totalScheduledFees) : '—'}
                </td>
                <td className="money">{formatMoney(totals.scheduled)}</td>
                <td className="money">{formatMoney(totals.paid)}</td>
                <td className="money">{formatMoney(totals.left)}</td>
                <td colSpan={can_write ? 3 : 2} />
              </tr>
            </tfoot>
          )}
        </Table>
      </TableWrap>
    </div>
  );
}
